/**
 * Admin Authentication Hook
 * Clean Architecture: Presentation Layer hook for admin authentication state
 * 
 * Manages admin authentication state and provides authentication methods.
 * 
 * Author: CleverAct AI Development Team
 * Created: 2025-07-19
 */

import { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { adminAuthService } from '../services/adminAuth';
import type { AdminUser } from '../services/admin/types';

// Admin authentication context
interface AdminAuthContextType {
  admin: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (admin: AdminUser, token: string) => void;
  logout: () => Promise<void>;
  hasPermission: (resource: string, action: string) => Promise<boolean>;
  checkPermission: (resource: string, action: string) => boolean;
  refreshAdmin: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

// Admin authentication provider
export const AdminAuthProvider = ({ children }: { children: ReactNode }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (adminAuthService.isAuthenticated()) {
          // Try to get current admin info
          const currentAdmin = await adminAuthService.getCurrentAdmin();
          setAdmin(currentAdmin);
        }
      } catch (error) {
        console.warn('Failed to initialize admin auth:', error);
        // Clear invalid token
        await adminAuthService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = (adminUser: AdminUser, token: string) => {
    setAdmin(adminUser);
  };

  // Logout function
  const logout = async () => {
    try {
      await adminAuthService.logout();
    } catch (error) {
      console.warn('Logout error:', error);
    } finally {
      setAdmin(null);
    }
  };

  // Permission checking (async - makes API call)
  const hasPermission = async (resource: string, action: string): Promise<boolean> => {
    try {
      return await adminAuthService.checkPermission(resource, action);
    } catch (error) {
      console.warn('Permission check failed:', error);
      return false;
    }
  };

  // Quick permission check from cached admin data
  const checkPermission = (resource: string, action: string): boolean => {
    if (!admin || !admin.permissions) return false;
    
    // Super admin has all permissions
    if (admin.role === 'super_admin') return true;
    
    const resourcePermissions = admin.permissions[resource] || [];
    return resourcePermissions.includes(action);
  };

  // Refresh admin data
  const refreshAdmin = async () => {
    try {
      if (adminAuthService.isAuthenticated()) {
        const currentAdmin = await adminAuthService.getCurrentAdmin();
        setAdmin(currentAdmin);
      }
    } catch (error) {
      console.warn('Failed to refresh admin data:', error);
      await logout();
    }
  };

  const value: AdminAuthContextType = {
    admin,
    isLoading,
    isAuthenticated: !!admin,
    login,
    logout,
    hasPermission,
    checkPermission,
    refreshAdmin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Hook to use admin authentication
export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

// Higher-order component for admin routes
export function withAdminAuth<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAdminAuth();

    if (isLoading) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to admin login
      window.location.href = '/admin/login';
      return null;
    }

    return <Component {...props} />;
  };
}

// Hook for permission-based UI rendering
export const useAdminPermission = (resource: string, action: string) => {
  const { checkPermission, admin } = useAdminAuth();
  const [hasPermission, setHasPermission] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPerm = async () => {
      if (!admin) {
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      try {
        const result = checkPermission(resource, action);
        setHasPermission(result);
      } catch (error) {
        console.warn('Permission check error:', error);
        setHasPermission(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPerm();
  }, [admin, resource, action, checkPermission]);

  return { hasPermission, isLoading };
};

export default useAdminAuth;
